import { BALANCE } from '../balance';
import type { Condition, RewardInput } from '../schemas/progression';
import type { SuggestionDefInput } from '../schemas/progressionDefs';

/**
 * Gentle Suggestions (content-schemas §4.4 + §16.3, economy-progression §9):
 * T01–T10 tutorial (1 star, auto-claimed, shown one at a time), O01–O08 optional (1 star, shown as features open),
 * rep_* repeatable pool (10–20 coins, never stars).
 */
const tutorialStar: RewardInput[] = [{ type: 'stars', amount: BALANCE.starsPer.tutorialTask }];
const optionalStars: RewardInput[] = [{ type: 'stars', amount: BALANCE.starsPer.optionalTask }];
const shopOpen: Condition[] = [{ type: 'unlocked', unlockId: 'unl_minimart' }];

export const TUTORIAL_SUGGESTIONS = [
  {
    id: 'T01',
    kind: 'tutorial',
    order: 1,
    titleKey: 'tutorial.T01.title',
    hintKey: 'tutorial.T01.1',
    criteria: { kind: 'conditions', all: [{ type: 'boxOpened', boxDefId: 'box_01_sleep' }] },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'sceneObject', sceneId: 'home', ref: 'box_01_sleep' },
  },
  {
    id: 'T02',
    kind: 'tutorial',
    order: 2,
    titleKey: 'tutorial.T02.title',
    hintKey: 'tutorial.T02.1',
    criteria: { kind: 'placed', where: { itemId: 'fur_bed_pine' }, count: 1 },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'ui', elementId: 'bag' },
  },
  {
    id: 'T03',
    kind: 'tutorial',
    order: 3,
    titleKey: 'tutorial.T03.title',
    hintKey: 'tutorial.T03.1',
    criteria: { kind: 'stat', stat: 'itemsRotated', target: 1, relative: false },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'ui', elementId: 'place.rotate' },
  },
  {
    id: 'T04',
    kind: 'tutorial',
    order: 4,
    titleKey: 'tutorial.T04.title',
    hintKey: 'tutorial.T04.1',
    criteria: { kind: 'placed', where: { itemId: 'fur_nightstand_pine', rightSpot: true }, count: 1 },
    rewards: tutorialStar,
    autoClaim: true,
  },
  {
    id: 'T05',
    kind: 'tutorial',
    order: 5,
    titleKey: 'tutorial.T05.title',
    hintKey: 'tutorial.T05.1',
    criteria: {
      kind: 'conditions',
      all: [
        { type: 'boxOpened', boxDefId: 'box_02_work' },
        { type: 'boxOpened', boxDefId: 'box_03_books' },
        { type: 'boxOpened', boxDefId: 'box_04_kitchen' },
      ],
    },
    rewards: tutorialStar,
    autoClaim: true,
  },
  {
    id: 'T06',
    kind: 'tutorial',
    order: 6,
    titleKey: 'tutorial.T06.title',
    hintKey: 'tutorial.T06.1',
    criteria: { kind: 'conditions', all: [{ type: 'miniGameCleared', miniGameId: 'mg_dusting', level: 1 }] },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'sceneObject', sceneId: 'home', ref: 'box_05_granny' },
  },
  {
    id: 'T07',
    kind: 'tutorial',
    order: 7,
    titleKey: 'tutorial.T07.title',
    hintKey: 'tutorial.T07.1',
    criteria: { kind: 'conditions', all: [{ type: 'chapterSeen', chapterId: 'ch1' }] },
    rewards: tutorialStar,
    autoClaim: true,
  },
  {
    id: 'T08',
    kind: 'tutorial',
    order: 8,
    titleKey: 'tutorial.T08.title',
    hintKey: 'tutorial.T08.1',
    criteria: { kind: 'placed', where: { itemId: 'dec_book_stack', rightSpot: true }, count: 1 },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'sceneObject', sceneId: 'home', ref: 'fur_bookshelf' },
  },
  {
    id: 'T09',
    kind: 'tutorial',
    order: 9,
    titleKey: 'tutorial.T09.title',
    // ICU {price} comes from dec_desk_lamp.buyPrice (story-dialogue §11.4).
    hintKey: 'tutorial.T09.1',
    hintPriceItemId: 'dec_desk_lamp',
    criteria: { kind: 'event', events: ['ITEM_PURCHASED'], count: 1 },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'ui', elementId: 'nav.catalog' },
  },
  {
    id: 'T10',
    kind: 'tutorial',
    order: 10,
    titleKey: 'tutorial.T10.title',
    hintKey: 'tutorial.T10.1',
    criteria: { kind: 'placed', where: { acquiredBy: 'purchase' }, count: 1 },
    rewards: tutorialStar,
    autoClaim: true,
    highlight: { kind: 'ui', elementId: 'bag' },
  },
] as const satisfies readonly SuggestionDefInput[];

export const OPTIONAL_SUGGESTIONS = [
  {
    id: 'O01',
    kind: 'optional',
    order: 1,
    titleKey: 'suggestion.O01.title',
    requires: shopOpen,
    criteria: { kind: 'stat', stat: 'productsDisplayed', target: 12, relative: false },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart' },
  },
  {
    id: 'O02',
    kind: 'optional',
    order: 2,
    titleKey: 'suggestion.O02.title',
    requires: [{ type: 'taskCompleted', taskId: 'O01' }],
    criteria: { kind: 'stat', stat: 'unitsSold', target: 5, relative: false },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart' },
  },
  {
    id: 'O03',
    kind: 'optional',
    order: 3,
    titleKey: 'suggestion.O03.title',
    requires: [{ type: 'taskCompleted', taskId: 'O02' }],
    criteria: { kind: 'stat', stat: 'stockBoxesBought', target: 1, relative: false },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart', panel: 'stock' },
  },
  {
    id: 'O04',
    kind: 'optional',
    order: 4,
    titleKey: 'suggestion.O04.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_npc_orders' }],
    criteria: { kind: 'stat', stat: 'ordersFulfilled', target: 1, relative: false },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart', panel: 'orders' },
  },
  {
    id: 'O05',
    kind: 'optional',
    order: 5,
    titleKey: 'suggestion.O05.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_packing' }],
    criteria: {
      kind: 'event',
      events: ['MINIGAME_COMPLETED'],
      where: { miniGameId: 'mg_packing', completed: true },
      count: 1,
    },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart', miniGameId: 'mg_packing' },
  },
  {
    id: 'O06',
    kind: 'optional',
    order: 6,
    titleKey: 'suggestion.O06.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_catalog_t2' }],
    criteria: { kind: 'placed', where: { roomId: 'minimart', kindsAny: ['decor', 'furniture'] }, count: 3 },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart', panel: 'catalog' },
  },
  {
    id: 'O07',
    kind: 'optional',
    order: 7,
    titleKey: 'suggestion.O07.title',
    requires: shopOpen,
    criteria: { kind: 'stat', stat: 'catPets', target: 3, relative: false },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'minimart' },
  },
  {
    id: 'O08',
    kind: 'optional',
    order: 8,
    titleKey: 'suggestion.O08.title',
    requires: [{ type: 'cozinessAtLeast', roomId: 'home', level: 2 }],
    criteria: { kind: 'placed', where: { tagsAny: ['plant'], zone: 'window' }, count: 1 },
    rewards: optionalStars,
    autoClaim: false,
    goto: { room: 'home' },
  },
] as const satisfies readonly SuggestionDefInput[];

/** Repeatable pool: no stars, 10–20 coins (BALANCE.repeatableRewardCoins), 3 visible at a time. */
export const REPEATABLE_SUGGESTIONS = [
  {
    id: 'rep_sell_5',
    kind: 'repeatable',
    order: 1,
    titleKey: 'suggestion.repeat.sell_5.title',
    requires: shopOpen,
    criteria: { kind: 'stat', stat: 'unitsSold', target: 5, relative: true },
    rewards: [{ type: 'coins', amount: BALANCE.repeatableRewardCoins.max }],
    autoClaim: false,
    goto: { room: 'minimart' },
  },
  {
    id: 'rep_right_spot_1',
    kind: 'repeatable',
    order: 2,
    titleKey: 'suggestion.repeat.right_spot_1.title',
    criteria: { kind: 'event', events: ['ITEM_PLACED'], where: { rightSpot: true, fromBag: true }, count: 1 },
    rewards: [{ type: 'coins', amount: BALANCE.repeatableRewardCoins.min }],
    autoClaim: false,
    goto: { room: 'home' },
  },
  {
    id: 'rep_minigame_1',
    kind: 'repeatable',
    order: 3,
    titleKey: 'suggestion.repeat.minigame_1.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_dusting' }],
    criteria: { kind: 'event', events: ['MINIGAME_COMPLETED'], where: { completed: true }, count: 1 },
    rewards: [{ type: 'coins', amount: 15 }],
    autoClaim: false,
    goto: { room: 'home', miniGameId: 'mg_dusting' },
  },
  {
    id: 'rep_order_1',
    kind: 'repeatable',
    order: 4,
    titleKey: 'suggestion.repeat.order_1.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_npc_orders' }],
    criteria: { kind: 'stat', stat: 'ordersFulfilled', target: 1, relative: true },
    rewards: [{ type: 'coins', amount: 15 }],
    autoClaim: false,
    goto: { room: 'minimart', panel: 'orders' },
  },
  {
    id: 'rep_photo_1',
    kind: 'repeatable',
    order: 5,
    titleKey: 'suggestion.repeat.photo_1.title',
    requires: [{ type: 'unlocked', unlockId: 'unl_photo_mode' }],
    criteria: { kind: 'stat', stat: 'photosTaken', target: 1, relative: true },
    rewards: [{ type: 'coins', amount: BALANCE.repeatableRewardCoins.min }],
    autoClaim: false,
    goto: { panel: 'photo' },
  },
  {
    id: 'rep_talk_1',
    kind: 'repeatable',
    order: 6,
    titleKey: 'suggestion.repeat.talk_1.title',
    requires: shopOpen,
    criteria: { kind: 'stat', stat: 'npcTalks', target: 1, relative: true },
    rewards: [{ type: 'coins', amount: BALANCE.repeatableRewardCoins.min }],
    autoClaim: false,
    goto: { panel: 'town' },
  },
] as const satisfies readonly SuggestionDefInput[];

export const SUGGESTIONS: readonly SuggestionDefInput[] = [
  ...TUTORIAL_SUGGESTIONS,
  ...OPTIONAL_SUGGESTIONS,
  ...REPEATABLE_SUGGESTIONS,
];
